import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { EventInventoryRecord, selectSelectedInventoryItems } from "@src/store/pricing/queryEventInventoryAllSlice";
import { pricingActions } from "@src/store";
import { Button, ButtonVariant } from "../button/Button";
import helperStyles from "@src/components/helpers.module.scss";
import { clx } from "../utils/stringUtils";
import BulkHoldListingsItem from "./BulkHoldListingsItem";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import styles from "./BulkHoldListings.module.scss";

type TBulkHoldListings = {
	handleClose?: () => void;
};

type TDurationOption = {
	value: string;
	label: string;
	addAmount: number | null;
	addUnit: moment.unitOfTime.DurationConstructor | null;
};

const DURATION_OPTIONS: Array<TDurationOption> = [
	{ value: "15m", label: "15 minutes", addAmount: 15, addUnit: "minutes" },
	{ value: "60m", label: "60 minutes", addAmount: 60, addUnit: "minutes" },
	{ value: "1d", label: "1 day", addAmount: 1, addUnit: "days" },
	{ value: "3d", label: "3 days", addAmount: 3, addUnit: "days" },
	{ value: "", label: "No Expiration", addAmount: null, addUnit: null },
];

type TState = {
	items: Array<EventInventoryRecord>;
	durationValue: string;
	comments: string;
};

const BulkHoldListings = ({ handleClose }: TBulkHoldListings) => {
	const dispatch = useAppDispatch();

	const items = useAppSelector(selectSelectedInventoryItems);
	const holdLoading = useAppSelector((state) => state.pricing.holdListings.loading);
	const releaseLoading = useAppSelector((state) => state.pricing.releaseListings.loading);

	const [state, setState] = useState<TState>({
		items,
		durationValue: "15m",
		comments: "",
	});

	useEffect(() => {
		setState((prev) => ({ ...prev, items }));
	}, [items]);

	const allOnHold = state.items.length > 0 && state.items.every((x) => x.On_Hold);
	const inFlight = holdLoading || releaseLoading;

	const handleDurationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setState((prev) => ({ ...prev, durationValue: e.target.value }));
	};

	const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setState((prev) => ({ ...prev, comments: e.target.value }));
	};

	const handleRemoveItem = (listingId: number) => {
		setState((prev) => ({ ...prev, items: prev.items.filter((x) => x.Listing_ID !== listingId) }));
	};

	const selectedDuration = useMemo(
		() => DURATION_OPTIONS.find((x) => x.value === state.durationValue) ?? DURATION_OPTIONS[0],
		[state.durationValue],
	);

	const appliedDurationLabel = selectedDuration.label;

	const buildExpirationDate = (): string => {
		if (!selectedDuration.addAmount || !selectedDuration.addUnit) return "";
		return moment().add(selectedDuration.addAmount, selectedDuration.addUnit).format("MM/DD/YYYY hh:mm A");
	};

	const handleHold = async () => {
		if (!state.items.length) return;
		try {
			await dispatch(
				pricingActions.holdListings({
					ListingIds: state.items.map((x) => x.Listing_ID),
					Comments: state.comments,
					ExpirationDate: buildExpirationDate(),
				}),
			).unwrap();
			toast.success("Listings placed on hold.");
			handleClose?.();
		} catch {
			toast.error("Failed to hold listings.");
			handleClose?.();
		}
	};

	const handleRelease = async () => {
		if (!state.items.length) return;
		try {
			await dispatch(
				pricingActions.releaseListings({
					ListingIds: state.items.map((x) => x.Listing_ID),
				}),
			).unwrap();
			toast.success("Listings released.");
			handleClose?.();
		} catch {
			toast.error("Failed to release listings.");
			handleClose?.();
		}
	};

	const mappedItems = state.items.map((x) => (
		<BulkHoldListingsItem
			key={x.Item_ID}
			data={x}
			appliedDurationLabel={appliedDurationLabel}
			onRemove={handleRemoveItem}
		/>
	));

	return (
		<div className={styles.root}>
			<EventContextHeader
				padding
				listings={state.items.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))}
			/>
			<div className={styles.top}>
				<div className={styles.left}>
					<div className={clx(styles.subHeader, styles.mb)}>Hold Rules</div>
					<div className={styles.fieldLabel}>Hold Duration</div>
					<select className={styles.durationSelect} value={state.durationValue} onChange={handleDurationChange}>
						{DURATION_OPTIONS.map((opt) => (
							<option key={opt.value || "no-exp"} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
					<div className={styles.helperText}>
						Listings will be temporarily hidden from marketplaces until the hold expires.
					</div>
					<div className={styles.fieldLabel}>Comments</div>
					<textarea
						className={styles.commentsTextarea}
						placeholder="Add an internal note about why these listings are being held"
						value={state.comments}
						onChange={handleCommentsChange}
					/>
					{state.items.length > 1 ? (
						<div className={styles.warning}>
							<div className={styles.warningHeader}>Warning! You have multiple listings selected.</div>
							<div className={styles.warningText}>
								Saving will overwrite any previous Hold Rules settings saved to these listings.
							</div>
						</div>
					) : null}
				</div>
				<div className={styles.right}>
					<div className={styles.rightTopHeaders}>
						<div className={styles.subHeader}>Target Listings</div>
						<div className={styles.subHeaderSecondary}>Applying to {state.items.length} items</div>
					</div>
					<div data-no-border="true" className={styles.singleListing}>
						<div className={clx(styles.secRowSeat, styles.headerSecRowSeat)}>
							<div className={clx(styles.itemTextPrimary, helperStyles.ellipsisDiv)}>Sec, Row, Seat</div>
						</div>
						<div className={styles.duration}>Duration</div>
						<div className={styles.existingComments}>Comments</div>
						<div className={styles.qty}>Qty</div>
						<div className={styles.priceLabel}>Price</div>
						<div className={styles.xContainer}></div>
					</div>
					<div className={styles.listings}>{mappedItems}</div>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button onClick={handleClose} variant={ButtonVariant.Secondary} disabled={inFlight}>
					Cancel
				</Button>
				{allOnHold ? (
					<Button onClick={handleRelease} isLoading={releaseLoading} disabled={!state.items.length || inFlight}>
						Release listings
					</Button>
				) : (
					<Button onClick={handleHold} isLoading={holdLoading} disabled={!state.items.length || inFlight}>
						Save changes
					</Button>
				)}
			</div>
		</div>
	);
};

export default BulkHoldListings;
